import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { ShieldCheck, KeyRound, PlayCircle, MessagesSquare, Compass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDate } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { resetOnboardingTour, relancerGuidePrincipal, relancerGuideMessagerie, demanderRelanceGuide } from '../../components/OnboardingTour';

export default function Profile() {
  const { user, agency, permissions, setUser, hasPerm } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const nav = useNavigate();

  const changePassword = async () => {
    if (pwd.next.length < 8) { toast.error('Le nouveau mot de passe doit faire au moins 8 caractères'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setPwdSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.next });
      toast.success('Mot de passe mis à jour');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(apiError(e, 'Impossible de mettre à jour le mot de passe'));
    } finally { setPwdSaving(false); }
  };

  // 29/09 : effacer le drapeau ne relançait rien — on navigue D'ABORD, puis on demande
  // au tableau de bord (qui, lui, ne se remonte pas tout seul) de rejouer le tour.
  // Meme condition que DashboardIndex (App.js) : sans ces droits, /dashboard renvoie
  // ailleurs, donc le guide du tableau de bord n'a aucun sens — et n'ouvrirait rien.
  const aUnTableauDeBord = hasPerm('manage_telegram') || hasPerm('sales_view');

  const restartTour = (which) => {
    // « Tout réinitialiser » pour un chatteur = son seul guide, celui de la messagerie.
    if (which === 'all' && !aUnTableauDeBord && hasPerm('msg_read')) {
      resetOnboardingTour();
      toast.success('Tous les guides réinitialisés — ouverture de la messagerie');
      nav('/dashboard/messages');
      setTimeout(() => demanderRelanceGuide('inbox'), 450);
      return;
    }
    if (which === 'inbox') {
      relancerGuideMessagerie();
      toast.success('Guide de la messagerie relancé — ouverture de la messagerie');
      nav('/dashboard/messages');
      setTimeout(() => demanderRelanceGuide('inbox'), 450);
      return;
    }
    if (which === 'main') {
      relancerGuidePrincipal();
      toast.success('Guide relancé — retour au tableau de bord');
    } else {
      resetOnboardingTour();
      toast.success('Tous les guides réinitialisés');
    }
    nav('/dashboard');
    setTimeout(() => demanderRelanceGuide('main'), 450);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/auth/me', { name });
      setUser(data);
      toast.success('Profil mis à jour');
    } catch (e) { toast.error(apiError(e, 'Échec de la mise à jour')); } finally { setSaving(false); }
  };

  const initials = (user?.name || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto fade-in" data-testid="profile-page">
      <PageHeader title="Profil" subtitle="Tes informations personnelles et ta sécurité" />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 mb-5">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="w-16 h-16"><AvatarFallback className="bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xl font-bold">{initials}</AvatarFallback></Avatar>
          <div>
            <div className="text-lg font-bold text-white" data-testid="profile-name">{user?.name}</div>
            <div className="text-sm text-slate-400">{user?.email}</div>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/30"><ShieldCheck size={11} className="mr-1" /> {user?.role}</Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400 text-[10px]">{permissions.length} permissions</Badge>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Nom complet</Label>
            <Input data-testid="profile-name-input" value={name} onChange={e => setName(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Email</Label>
            <Input value={user?.email || ''} disabled className="bg-white/5 border-white/10 text-slate-500 h-10" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button data-testid="profile-save-button" className="bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer les changements'}</Button>
          <span className="text-xs text-slate-500">Membre de {agency?.name} depuis le {fmtDate(user?.created_at)}</span>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 text-white font-semibold text-sm mb-1"><PlayCircle size={15} className="text-indigo-400" /> Guides de démarrage</div>
        <p className="text-xs text-slate-400 mb-4">Relance les guides interactifs — parfait pour former les nouveaux employés qui n&apos;ont pas accès aux autres sections.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {/* 29/09 : un chatteur n'a PAS de tableau de bord (App.js le redirige) — ce bouton
              ne pouvait rien lui ouvrir. On ne l'affiche qu'à ceux qui en ont un. */}
          {aUnTableauDeBord && (
          <Button data-testid="restart-main-tour" variant="outline" className="border-indigo-500/30 bg-indigo-500/[0.06] text-indigo-200 hover:bg-indigo-500/15 h-auto py-3 flex flex-col items-start gap-1" onClick={() => restartTour('main')}>
            <span className="flex items-center gap-2 font-semibold"><Compass size={14} /> Guide du tableau de bord</span>
            <span className="text-[11px] text-slate-400 font-normal">le tour complet · comptes → messages → vault → scripts → DropFans → employés</span>
          </Button>
          )}
          {/* Only show the inbox guide to users who can actually open the Inbox —
              a tracking-only VA (no msg_read) would otherwise be navigated to a page
              they can't load (the original "Something went wrong" class of bug). */}
          {hasPerm('msg_read') && (
          <Button data-testid="restart-inbox-tour" variant="outline" className="border-violet-500/30 bg-violet-500/[0.06] text-violet-200 hover:bg-violet-500/15 h-auto py-3 flex flex-col items-start gap-1" onClick={() => restartTour('inbox')}>
            <span className="flex items-center gap-2 font-semibold"><MessagesSquare size={14} /> Guide de la messagerie</span>
            <span className="text-[11px] text-slate-400 font-normal">tout le parcours · comptes · filtres · scripts · vault · envoi · notes &amp; pièges</span>
          </Button>
          )}
        </div>
        <button data-testid="restart-all-tours" onClick={() => restartTour('all')} className="text-[11px] text-slate-500 hover:text-slate-300 mt-3 underline">Réinitialiser tous les guides</button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 text-white font-semibold text-sm mb-4"><KeyRound size={15} className="text-amber-400" /> Sécurité</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Input data-testid="current-password" type="password" placeholder="Mot de passe actuel" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" />
          <Input data-testid="new-password" type="password" placeholder="Nouveau mot de passe" value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" />
          <Input data-testid="confirm-password" type="password" placeholder="Confirme le nouveau mot de passe" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} className="bg-white/5 border-white/10 text-white h-10" />
        </div>
        <Button data-testid="update-password-button" variant="outline" className="mt-4 border-white/10 bg-white/5 text-white hover:bg-white/10" onClick={changePassword} disabled={pwdSaving || !pwd.current || !pwd.next}>{pwdSaving ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}</Button>
      </div>
    </div>
  );
}
